/**
 * Route admin `/tutor/admin/animations` — rubrique « Animations SKILLCARDS ».
 *
 * Toutes les animations créées par l'IA depuis une SKILLCARD (moteur JRAF) :
 * leur carte, leur état (génération, validité, défauts de mise en page et de
 * rythme revus avec les règles du jour, voix enregistrées) et les commandes —
 * tester / modifier, régénérer (avec consignes), supprimer, publier.
 *
 * Publication : dans l'app (lot 4 : version immuable revalidée par le
 * serveur, partie « À toi » réservée aux élèves qui ont la carte, lien
 * public) ; en vidéo verticale — YouTube Short, TikTok, Reels — depuis
 * l'écran « Tester » (lot 7 : rendu image par image par le worker vidéo,
 * voix de synthèse signalée sur les parties sans voix enregistrée).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Clapperboard, Mic, Music, Search, Send, Trash2 } from 'lucide-react';
import { subjectLabel } from '@jeraisonne/db/curriculum';
import { cn, MathText, Paginator } from '@jeraisonne/ui';
import { formatCardLevels } from '@/features/cards/card-faces';
import { AnimationGenerationControls } from '@/features/cards/card-animation-panel';
import {
  animationHealth,
  readinessOf,
  type AnimationHealth,
} from '@/features/animations/animation-health';
import { AnimationAppPublish } from '@/features/animations/animation-publish-controls';
import {
  hasUnpublishedChanges,
  isGenerating,
  useAnimationsList,
  useDeleteAnimation,
  type AnimationListRow,
} from '@/lib/animations/api';
import { usePagedList } from '@/lib/use-paged-list';

type Filter = 'all' | 'fix' | 'ready' | 'generating' | 'archived';

const FILTERS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: 'all', label: 'Toutes' },
  { value: 'fix', label: 'À reprendre' },
  { value: 'ready', label: 'Prêtes' },
  { value: 'generating', label: 'En génération' },
  { value: 'archived', label: 'Archivées' },
];

const STATUS_LABEL: Record<AnimationListRow['status'], string> = {
  draft: 'Brouillon',
  review: 'En relecture',
  published: 'Publiée',
  archived: 'Archivée',
};

const STATUS_STYLE: Record<AnimationListRow['status'], string> = {
  draft: 'bg-ink-100 text-ink-600',
  review: 'bg-accent-50 text-accent-600',
  published: 'bg-xp-100 text-xp-700',
  archived: 'bg-ink-100 text-ink-400',
};

/** recherche : sans accents ni ligatures (« stoechio » trouve « stœchiométrique ») */
function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae');
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'font-display min-h-11 rounded-full px-3 py-1 text-[13px] font-semibold transition-colors active:scale-[0.98]',
        active ? 'bg-primary-500 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
      )}
    >
      {children}
    </button>
  );
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold',
        className,
      )}
    >
      {children}
    </span>
  );
}

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** Publier : dans l'app (lot 4) ; en vidéo (lot 7) depuis l'écran « Tester ». */
function PublishPanel({ row, health }: { row: AnimationListRow; health: AnimationHealth | null }) {
  const ready = readinessOf(health) === 'ready';
  const missingVoices = health ? health.scenes - health.recorded : null;
  const live = row.status === 'published' && !!row.published_version;
  return (
    <div className="border-ink-200 space-y-3 rounded-xl border p-3">
      <section aria-label="Dans l’app" className="space-y-1">
        <h3 className="font-display text-ink-900 text-sm font-bold">Dans l’app</h3>
        <AnimationAppPublish
          row={row}
          health={health ? { valid: health.valid, quality: health.quality.length } : null}
        />
      </section>
      <section aria-label="En vidéo" className="border-ink-100 space-y-1.5 border-t pt-2">
        <h3 className="font-display text-ink-900 text-sm font-bold">
          En vidéo · YouTube Short, TikTok, Reels
        </h3>
        <p className="text-ink-600 text-[13px]">
          {live
            ? 'La vidéo montre la version publiée : aperçu à valider, puis publication.'
            : 'Publie d’abord dans l’app : la vidéo montre la version publiée.'}
          {missingVoices !== null && missingVoices > 0 && (
            <>
              {' '}
              {missingVoices} partie{missingVoices > 1 ? 's' : ''} sans ta voix : voix de synthèse
              (IA), signalée au générique et dans la description.
            </>
          )}
          {!ready && health?.valid && <> Défauts de mise en page ou de rythme à revoir.</>}
        </p>
        <Link
          to="/tutor/admin/animations/$animationId"
          params={{ animationId: row.id }}
          hash="video"
          aria-disabled={!live}
          className={cn(
            'border-ink-200 text-ink-700 hover:bg-ink-50 inline-flex min-h-11 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold',
            !live && 'pointer-events-none opacity-50',
          )}
        >
          <Clapperboard className="h-4 w-4" aria-hidden /> Exporter en vidéo
        </Link>
      </section>
    </div>
  );
}

function DeleteButton({ row }: { row: AnimationListRow }) {
  const del = useDeleteAnimation();
  const [confirm, setConfirm] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  // le bouton cliqué disparaît : le focus va à « Annuler » (choix sans risque)
  useEffect(() => {
    if (confirm) cancelRef.current?.focus();
  }, [confirm]);
  if (!confirm)
    return (
      <button
        type="button"
        onClick={() => setConfirm(true)}
        className="text-error-600 hover:bg-error-50 flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold"
      >
        <Trash2 className="h-4 w-4" aria-hidden /> Supprimer
      </button>
    );
  return (
    <div
      role="group"
      aria-label="Confirmer la suppression"
      className="bg-error-50 flex flex-wrap items-center gap-2 rounded-xl px-3 py-1.5"
    >
      <span className="text-error-600 text-sm font-semibold">
        {row.status === 'published'
          ? 'Supprimer cette animation ? Elle disparaît aussi de l’app et son lien ne marchera plus. La SKILLCARD reste.'
          : 'Supprimer cette animation ? La SKILLCARD reste.'}
      </span>
      <button
        type="button"
        disabled={del.isPending}
        onClick={() => del.mutate(row.id)}
        className="bg-error-600 min-h-11 rounded-xl px-3 text-sm font-bold text-white disabled:opacity-60"
      >
        Oui, supprimer
      </button>
      <button
        ref={cancelRef}
        type="button"
        onClick={() => setConfirm(false)}
        className="text-ink-700 min-h-11 rounded-xl px-3 text-sm font-semibold"
      >
        Annuler
      </button>
      {del.error && (
        <p role="alert" className="text-error-600 w-full text-xs">
          {del.error.message}
        </p>
      )}
    </div>
  );
}

function AnimationItem({ row, health }: { row: AnimationListRow; health: AnimationHealth | null }) {
  const [publishing, setPublishing] = useState(false);
  const card = row.card;
  const meta = card
    ? [card.levels.length ? formatCardLevels(card.levels) : null, subjectLabel(card.subject)]
        .filter(Boolean)
        .join(' · ')
    : '';
  const running = isGenerating(row);
  return (
    <li className="p-3" data-testid={`admin-animation-${row.id}`}>
      <div className="flex items-start gap-3">
        <div className="bg-ink-100 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
          <Clapperboard className="text-ink-700 h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-ink-900 text-[15px] font-bold">
            <MathText>{row.title || card?.title || 'Animation'}</MathText>
          </h2>
          {card && (
            <p className="text-ink-500 mt-0.5 text-[13px]">
              <Link
                to="/tutor/cards/$cardId"
                params={{ cardId: card.id }}
                className="text-primary-600 font-semibold hover:underline"
              >
                <MathText>{card.title}</MathText>
              </Link>
              {meta && <> · {meta}</>}
            </p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge className={STATUS_STYLE[row.status]}>
              {STATUS_LABEL[row.status]}
              {row.status === 'published' && row.published_version
                ? ` · v${row.published_version}`
                : ''}
            </Badge>
            {hasUnpublishedChanges(row) && (
              <Badge className="bg-warning-50 text-ink-700">Modifications non publiées</Badge>
            )}
            {!running && health && health.quality.length > 0 && (
              <Badge className="bg-warning-50 text-ink-700">
                {health.quality.length} défaut{health.quality.length > 1 ? 's' : ''} de mise en
                page, de rythme ou de figure
              </Badge>
            )}
            {!running && health?.valid && health.quality.length === 0 && (
              <Badge className="bg-xp-100 text-xp-700">Prête à publier</Badge>
            )}
            {!running && health && !health.valid && (
              <Badge className="bg-error-50 text-error-600">
                {health.errors.length} erreur{health.errors.length > 1 ? 's' : ''} à corriger
              </Badge>
            )}
            {health?.valid && (
              <Badge className="bg-ink-100 text-ink-600">
                <Mic className="h-3 w-3" aria-hidden />
                <span>
                  Voix {health.recorded}/{health.scenes}
                </span>
              </Badge>
            )}
            {row.generated_at && (
              <span className="text-ink-500 text-xs">
                générée le {dateFmt.format(new Date(row.generated_at))}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 space-y-2 sm:pl-[3.25rem]">
        <AnimationGenerationControls
          row={row}
          cardId={row.card_id}
          showTestLink
          testLabel="Tester / modifier"
          validity={health ? { valid: health.valid, errors: health.errors.length } : undefined}
          compact
          actions={
            <>
              <button
                type="button"
                aria-expanded={publishing}
                onClick={() => setPublishing((p) => !p)}
                className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-11 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold"
              >
                <Send className="h-4 w-4" aria-hidden /> Publier…
              </button>
              <DeleteButton row={row} />
            </>
          }
        />
        {publishing && !running && <PublishPanel row={row} health={health} />}
      </div>
    </li>
  );
}

export function TutorAdminAnimationsPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const list = useAnimationsList(filter === 'archived');
  const rows = useMemo(() => list.data ?? [], [list.data]);

  // validation refaite sur place (règles du jour), une fois par version de la ligne
  // validation refaite seulement pour les lignes changées (`updated_at`) : la
  // liste relue pendant une génération ne revalide pas tout
  const cache = useRef(new Map<string, AnimationHealth | null>());
  const healths = useMemo(() => {
    const m = new Map<string, AnimationHealth | null>();
    const next = new Map<string, AnimationHealth | null>();
    for (const r of rows) {
      if (isGenerating(r)) {
        m.set(r.id, null);
        continue;
      }
      const key = `${r.id}|${r.updated_at}`;
      const h = cache.current.has(key) ? cache.current.get(key)! : animationHealth(r.draft);
      next.set(key, h);
      m.set(r.id, h);
    }
    cache.current = next;
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    const words = norm(query).split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      const h = healths.get(r.id) ?? null;
      const generating = isGenerating(r);
      if (filter === 'archived' && r.status !== 'archived') return false;
      if (filter === 'generating' && !generating) return false;
      if (filter === 'ready' && (generating || readinessOf(h) !== 'ready')) return false;
      if (
        filter === 'fix' &&
        (generating || (readinessOf(h) === 'ready' && r.generation_status !== 'failed'))
      )
        return false;
      if (words.length === 0) return true;
      const hay = norm(`${r.title} ${r.card?.title ?? ''}`);
      return words.every((w) => hay.includes(w));
    });
  }, [rows, healths, filter, query]);

  const { items, paginator } = usePagedList(filtered, {
    resetKeys: [query, filter],
    unit: 'animations',
    label: 'Pages des animations',
  });

  const totals = useMemo(() => {
    let ready = 0;
    let fix = 0;
    for (const r of rows) {
      if (isGenerating(r) || r.status === 'archived') continue;
      if (readinessOf(healths.get(r.id) ?? null) === 'ready') ready++;
      else fix++;
    }
    return { ready, fix };
  }, [rows, healths]);

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="font-display text-ink-900 text-[22px] font-extrabold">
            Animations SKILLCARDS
          </h1>
          {list.data && (
            <p className="text-ink-500 mt-0.5 text-[13px]" data-testid="admin-animations-totals">
              {rows.length} animation{rows.length > 1 ? 's' : ''}
              {filter !== 'archived' &&
                ` · ${totals.ready} prête${totals.ready > 1 ? 's' : ''} · ${totals.fix} à reprendre`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4">
          <Link
            to="/tutor/admin/animations/sons"
            className="text-primary-600 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold hover:underline"
          >
            <Music className="h-4 w-4" aria-hidden /> Sons
          </Link>
          <Link
            to="/tutor/admin/animations/lab"
            className="text-primary-600 inline-flex min-h-11 items-center text-sm font-semibold hover:underline"
          >
            Banc du lecteur
          </Link>
        </div>
      </div>

      <div className="mb-4 space-y-2">
        <label className="relative block">
          <span className="sr-only">Rechercher une animation ou sa SKILLCARD</span>
          <Search
            className="text-ink-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Animation ou SKILLCARD"
            className="border-ink-200 text-ink-900 focus:border-primary-400 min-h-11 w-full rounded-xl border bg-white py-2 pl-9 pr-3 text-[15px] outline-none"
          />
        </label>
        <div role="group" aria-label="État" className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map((f) => (
            <Pill key={f.value} active={filter === f.value} onClick={() => setFilter(f.value)}>
              {f.label}
            </Pill>
          ))}
        </div>
      </div>

      {list.isLoading && <p className="text-ink-500 text-sm">Chargement…</p>}
      {list.error && (
        <p role="alert" className="text-error-600 text-sm">
          Impossible de lire les animations.
        </p>
      )}
      {list.data && filtered.length === 0 && (
        <div className="border-ink-200 rounded-2xl border bg-white p-6 text-center">
          <p className="text-ink-600 text-sm">
            {rows.length === 0
              ? 'Aucune animation pour l’instant. Ouvre une SKILLCARD et lance « Créer l’animation (IA) ».'
              : 'Aucune animation ne correspond.'}
          </p>
          {rows.length === 0 && (
            <Link
              to="/tutor/cards"
              className="text-primary-600 mt-2 inline-flex min-h-11 items-center text-sm font-semibold hover:underline"
            >
              Voir les SKILLCARDS
            </Link>
          )}
        </div>
      )}
      {items.length > 0 && (
        <ul className="divide-ink-100 border-ink-200 divide-y rounded-2xl border bg-white">
          {items.map((r) => (
            <AnimationItem key={r.id} row={r} health={healths.get(r.id) ?? null} />
          ))}
        </ul>
      )}
      <div className="mt-4">
        <Paginator {...paginator} />
      </div>
    </div>
  );
}
